/**
 * Game Rules descriptor + Campaign State types (STEP 3).
 * The mechanical shape of a character sheet is rule-system specific (A-01 was
 * decided: BOTH systems are supported; each campaign picks exactly one).
 */
import type { EntityBase, LocalizedString, SourceRef } from './shared';

export type RuleSystemId = 'fate' | 'dnd5e-atla' | 'mixed';

export interface RuleSystemDescriptor {
  id: RuleSystemId;
  label: LocalizedString;
  sourceDocument: string;
}

export const RULE_SYSTEMS: Record<RuleSystemId, RuleSystemDescriptor> = {
  fate: {
    id: 'fate',
    label: { tr: 'Fate Condensed', en: 'Fate Condensed' },
    sourceDocument: 'Fate v0.04 - Türkçe Playtest 1 - CS',
  },
  'dnd5e-atla': {
    id: 'dnd5e-atla',
    label: { tr: 'DnD 5e ATLA (ev yapımı)', en: 'D&D 5e ATLA Homebrew' },
    sourceDocument: 'ATLA 5e Homebrew',
  },
  mixed: {
    id: 'mixed',
    label: { tr: 'Karma (DnD 5e + Fate)', en: 'Mixed (D&D 5e + Fate)' },
    sourceDocument: 'D&D 5e SRD 5.1 (CC-BY-4.0) · ATLA Homebrew · Fate Condensed',
  },
};

// ---------------------------------------------------------------- Campaign

export interface Campaign extends EntityBase {
  name: string;
  ruleSystem: RuleSystemId;
  /** Freeform — source lists multiple eras until A-07 is answered. */
  era: string | null;
  synopsis: string;
  sessions: Session[];
  characters: Character[];
  npcs: Npc[];
  enemies: Enemy[];
  encounters: Encounter[];
  locations: Location[];
  maps: GameMap[];
  factions: Faction[];
  items: Item[];
  storyNodes: StoryNode[];
  notes: Note[];
  /** Party panel battle grid — optional so older campaign files load as-is. */
  battle?: BattleState | null;
  /**
   * GM screen panel ids (`GmPanelId`) this table keeps pinned at the top of
   * the screen, in pin order. Optional — every campaign file written before
   * the pin feature existed loads as-is — and stored WITH the campaign, so it
   * persists through the one autosave/export path (no second storage mechanism).
   */
  pinnedGmPanels?: string[];
  /** What the broadcast screen shows (optional — see BroadcastSettings). */
  broadcast?: BroadcastSettings;
}

export interface Session extends EntityBase {
  campaignId: string;
  number: number;
  title: LocalizedString;
  date: string | null;
  summary: string;
  plannedContent: string;
  linkedEncounterIds: string[];
  linkedLocationIds: string[];
  linkedStoryNodeIds: string[];
  /** Verbatim GM record — the app never does advancement arithmetic (A-06). */
  advancementNotes: string;
}

// ---------------------------------------------------------------- Sheets (A-02)

export type FateAspectLabel =
  | 'high-concept'
  | 'trouble'
  | 'relationship'
  | 'free';

/** Character's nation/element — drives the bending picker + name hints (P3). */
export type AvatarElement = 'fire' | 'earth' | 'water' | 'air' | 'non-bender';

/**
 * A bending art with a soft 1–3 level. Fate is aspect-based; the sources do
 * not define bending "levels" for Fate, so this is a GM tracker, not a
 * mechanic (flagged ambiguity — see docs/phase3-charcreation-rules.md).
 */
export interface BendingPick {
  /** Discipline name ("Metalbending") or supplementary art ("Sandbending"). */
  art: string;
  level: 1 | 2 | 3;
  /** GM freeform note: which path/tradition, source, caveat… */
  note: string;
}

export interface FateSheet {
  kind: 'fate';
  aspects: { label: FateAspectLabel; text: string }[]; // 1 HC + 1 trouble + 1 rel + 2 free
  /** One of the 19 Fate skills → adjective-ladder rank (numeric, 0 = Düz). */
  skills: Record<string, number>;
  stunts: { name: string; text: string; refreshCost: number }[]; // max 5, min refresh 1
  refresh: number;
  fatePoints: number;
  stress: { beden: number; irade: number };
  consequences: {
    severity: 'hafif' | 'orta' | 'agir';
    text: string;
    value: 2 | 4 | 6;
    /**
     * Büyük Adım (Fate p.39): a moderate/heavy consequence that has entered
     * recovery ("iyileşme süreci"); one already in recovery heals and may be
     * cleared at the next major step. Optional: a sheet written before the
     * advancement flow existed carries no flag, which reads as "not in
     * recovery" — exactly what it meant then.
     */
    recovery?: boolean;
  }[];
  /** Phase 3 additions (optional — older autosaves stay valid). */
  element?: AvatarElement | null;
  bending?: BendingPick[];
  /** Phase 4: starting kit + optional Silah/Zırh Değerleri (Fate CS p.57). */
  gear?: GearPick[];
  weaponValue?: number;
  armorValue?: number;
}

/**
 * The blocks a printed 5e-style sheet carries. Shared by the D&D-ATLA sheet and
 * the MIXED sheet: both print a saving-throw list, a skill list, armour class
 * with its formula, hit points, personality and background. Every field is
 * optional so autosaves written before the block existed still load.
 */
/**
 * A bending technique the player or the GM wrote for one character — the
 * table's homebrew next to the books' list. It lives on the sheet, so it
 * travels with the character file, and its id (`custom-…`) sits in
 * `techniqueIds` like any other: it counts toward the level's known limit.
 */
export interface CustomTechnique {
  id: string;
  name: LocalizedString;
  /** One of the sheet's own disciplines (Firebending, Universal, …). */
  discipline: string;
  castingTime: string | null;
  range: string | null;
  components: string | null;
  duration: string | null;
  description: LocalizedString;
  /** Who wrote it: a player's own idea still waits for the GM's nod at the table. */
  createdBy: 'player' | 'gm';
  createdAt: string;
}

export interface SheetExtras {
  /** Techniques written for this character (src/domain/custom-techniques.ts). */
  customTechniques?: CustomTechnique[];
  /**
   * The cantrips the player picked (src/content/cantrips.ts). The class's fixed
   * cantrips are not stored — they follow from the class. Optional: a sheet
   * written before cantrips existed simply has none picked.
   */
  cantripIds?: string[];
  /** SRD armour row id, or null for unarmoured. */
  armorId?: string | null;
  shield?: boolean;
  hp?: { current: number; max: number; temp: number };
  speed?: number;
  inspiration?: boolean;
  exhaustion?: number;
  deathSaves?: { successes: number; failures: number };
  conditions?: string[];
  /** Ability ids ("dex", "con") and skill ids from src/content/dnd5e.ts. */
  saveProficiencies?: string[];
  /**
   * Legacy free picks, written before 5d. The sheet's real proficiencies are
   * derived: lineage picks + the background's two. Kept so autosaves made
   * before the change keep the skills their player chose.
   */
  skillProficiencies?: string[];
  /** Background id from src/content/backgrounds.ts — grants two skills + a feature. */
  backgroundId?: string | null;
  /** A Player Manual background's "choose N from …" skill picks (pm-backgrounds.ts). */
  backgroundSkillChoices?: string[];
  /** The sub-origin named on the lineage page (a city, tribe, temple, clan). */
  origin?: string | null;
  /** Exactly two skill ids, each on the lineage's own "Choose 2" list (pp.11–14). */
  lineageSkillChoices?: string[];
  /** "Lessons of the Schools" (Fire Nation, p.13): proficiency bonus doubled. */
  expertiseSkills?: string[];
  /** The "conditional" line under saving throws. */
  saveConditional?: string;
  personality?: { traits: string; ideal: string; bond: string; flaw: string };
  background?: string;
  backgroundFeature?: string;
  backgroundStory?: string;
  gender?: string;
  age?: string;
  height?: string;
  features?: string;
  proficiencies?: string;
  /**
   * The rest of the printed character-details strip (weight, eyes, skin,
   * hair), alignment, "Allies & Organizations" and the trinket line — the
   * blocks the reference D&D sheet (`preex voran.pdf` p.2) carries. Optional,
   * so every sheet written before the D&D-style sheet existed still loads.
   */
  weight?: string;
  eyes?: string;
  skin?: string;
  hair?: string;
  alignment?: string;
  allies?: string;
  trinket?: string;
  /** Coins by denomination (the ATLA 5e Homebrew economy: copper, silver, gold). */
  purse?: { cp: number; sp: number; gp: number };
}

export interface DnD5eSheet extends SheetExtras {
  kind: 'dnd5e-atla';
  /** Content id from src/content/bender-progression LINEAGES (sub-lineages allowed). */
  lineage: string | null;
  era: string | null;
  classes: { name: string; level: number; specialization: string | null }[];
  abilityScores: Record<'str' | 'dex' | 'con' | 'int' | 'wis' | 'cha', number>;
  techniqueSlots: {
    tier: 'practiced' | 'trained' | 'mastered';
    count: number;
  }[];
  /** → content/techniques.json ids */
  techniqueIds: string[];
  /** → content/fighting-techniques.json ids */
  fightingTechniqueIds: string[];
  /** → content/feats.json ids */
  featIds: string[];
  /** Downtime training: max 2 concurrent, DC15 → TP (source verbatim). */
  training: {
    text: string;
    dc: number;
    tp: number;
    startedSession: number | null;
  }[];
  gold: number | null;
  /** Phase 4: the starting kit the player ticked (ATLA 5e Homebrew p.77 / p.10). */
  gear?: GearPick[];
  /** Which curated starting kit the techniques below came from, if any. */
  spellKitId?: string | null;
  /** GM overrides for the feat/fighting counts (defaults are derived from level). */
  fightingTechniqueLimit?: number;
  featLimit?: number;
}

/** One line of starting equipment (Phase 4; optional on older autosaves). */
export interface GearPick {
  id: string;
  name: LocalizedString;
  qty: number;
  /** 'weapon' | 'armor' | 'gear' | 'tool' | 'material' | 'consumable' */
  kind: string;
  /** Printed damage/properties line, verbatim where the source has one. */
  note: string | null;
  /** Fate CS p.57 optional values, when the line carries them. */
  weaponValue?: number | null;
  armorValue?: number | null;
  /**
   * Inventory shelf for the Party panel and the printed inventory page:
   * 'weapon' | 'armor' | 'potion' | 'poison' | 'remedy' | 'kit' | 'tool' |
   * 'gear' | 'valuable'. Optional — a line written before it existed is
   * shelved by its `kind`.
   */
  category?: string;
  /** The catalogue entry this line came from (src/content/consumables.ts …). */
  catalogId?: string | null;
  /** Weight in lb when the source prints one. */
  weight?: number | null;
  /** Who handed it over: 'gm' for a GM gift from the Party panel. */
  givenBy?: 'gm' | null;
}

/** The six 5e abilities. */
export type AbilityScoreMap = Record<'str' | 'dex' | 'con' | 'int' | 'wis' | 'cha', number>;

/**
 * MIXED sheet (Phase 5): D&D 5e dice and maths, Avatar setting instead of
 * race/class, Fate aspects on top. Every derived number lives in
 * src/domain/mixed-sheet.ts — this is storage only, so an older autosave
 * without the optional fields still loads.
 */
export interface MixedSheet extends SheetExtras {
  kind: 'mixed';
  /** Nation (a ATLA 5e Homebrew lineage id) replaces the D&D race. */
  lineage: string | null;
  element: AvatarElement | null;
  /** 'bender' | 'weaponsmaster' | 'tech-engineer' — replaces the D&D class. */
  calling: string;
  level: number;
  abilityScores: AbilityScoreMap;
  abilityMethod: 'standard-array' | 'point-buy' | 'rolled';
  /** The six 4d6 groups when the scores were rolled (audit trail). */
  abilityRolls?: number[][];
  /** Two saving-throw proficiencies, by ability id. */
  saveProficiencies: string[];
  /** Skill ids from src/content/dnd5e.ts. */
  skillProficiencies: string[];
  /** SRD armour row id, or null for unarmoured; drives AC. */
  armorId: string | null;
  shield: boolean;
  /** GM override when the table uses a rule the app does not model. */
  acOverride: number | null;
  hp: { current: number; max: number; temp: number };
  hitDie: number;
  speed: number;
  inspiration: boolean;
  exhaustion: number;
  deathSaves: { successes: number; failures: number };
  conditions: string[];
  /** Bending techniques used as spells (content/techniques.json ids). */
  techniqueIds: string[];
  techniqueSlots: { tier: 'practiced' | 'trained' | 'mastered'; count: number }[];
  fightingTechniqueIds: string[];
  featIds: string[];
  /** SRD weapon row ids the character actually carries. */
  weaponIds: string[];
  gear: GearPick[];
  gold: number | null;
  /** The Fate half: aspects, invoked for advantage and compelled for inspiration. */
  aspects: { label: FateAspectLabel; text: string }[];
  fatePoints: number;
  era: string | null;
  /** Free text, the way a printed 5e sheet carries it (optional). */
  features?: string;
  proficiencies?: string;
  /** Identity block from the printed sheet. */
  background?: string;
  gender?: string;
  age?: string;
  height?: string;
  /** PERSONALITY / IDEAL / BOND / FLAW block. */
  personality?: { traits: string; ideal: string; bond: string; flaw: string };
  backgroundFeature?: string;
  backgroundStory?: string;
  /** The "conditional" line under saving throws. */
  saveConditional?: string;
  /** GM overrides for the technique/feat counts (defaults are derived). */
  fightingTechniqueLimit?: number;
  featLimit?: number;
  /**
   * RACE + CLASS (the D&D-style mixed builder). All optional: a sheet with no
   * `classId` derives exactly as it always did (the `calling` path), so every
   * autosave, `.atlagm` and character file written before races and classes
   * existed loads and prints unchanged.
   *
   * `classId` → src/content/classes.ts (Airbender, Earthbender, Firebender,
   * Waterbender, Chi Blocker, Weaponsmaster, Tech-Engineer); `subclassId` is
   * the temple / calling / path / circle / archetype / specialization;
   * `classOptions` the level-1/2 picks (Earthbender combat style, Chi
   * Blocker fighting style); `classSkillChoices` the class's "choose N".
   */
  classId?: string | null;
  subclassId?: string | null;
  classOptions?: string[];
  classSkillChoices?: string[];
  /** Human (ATLA 5e Homebrew p.11): 'standard' (+1 to all) or 'variant'. */
  species?: 'standard' | 'variant';
  /** Variant human: the two abilities that got +1 … */
  speciesAbilityPicks?: string[];
  /** … the free skill … */
  speciesSkill?: string | null;
  /** … and the free feat (a content/feats.json id). */
  speciesFeat?: string | null;
  /** The scores before the species increase (audit trail for the sheet). */
  baseAbilityScores?: AbilityScoreMap;
  /** Languages written on the sheet (Common + the nation's). */
  languages?: string[];
  /** Experience points, when the table tracks them (milestone tables leave it empty). */
  xp?: number | null;
}

export type CharacterSheet = FateSheet | DnD5eSheet | MixedSheet;

/**
 * GM grants (phase 7 §5) — everything the GM handed this character outside the
 * sheet's own derivation. Optional and additive: a character written before
 * this existed has no `grants` key at all and derives/prints exactly as before.
 *
 * `items` is provenance that travels with the character file; the live copy of
 * the truth is Item.ownerId (both are written in one edit and never disagree —
 * see src/state/grants.ts). `feats` is provenance only: it never feeds the
 * sheet's feat budget (that belongs to the sheet editors), it records that the
 * GM granted the feat so the Party panel can list and revoke it.
 */
export interface CharacterGrants {
  /** Skill ids from the SRD list, granted on top of lineage/background. */
  skills?: string[];
  /** Campaign item ids this character carries by GM grant. */
  items?: string[];
  /** Feat ids granted by the GM (provenance only — see above). */
  feats?: string[];
  /** Free-text GM note (the only free-text grant: no list exists for it). */
  note?: string;
}

/**
 * One warning a level change raised and deliberately did NOT resolve by
 * dropping data (A-06: the app never silently deletes a technique, a feat or
 * an HP value the new level does not cover). `key` is an `advance.warn.*`
 * i18n key so the record reads in whichever locale it is opened in.
 */
export interface AdvancementWarning {
  key: string;
  params: Record<string, string | number>;
}

/**
 * One taken step, recorded on the character itself (the already-persisted
 * record — no second store). `option` is the source option that was taken
 * (`swap`, `add-stunt`, `level`, …), `parts` the extra pieces of a major
 * step (`hc`, `consequence:orta`, `refresh`, …), `targets` what it touched
 * (skill names, stunt, aspect index), `from`/`to` the rank or level before
 * and after, and `sources` the printed pages the step came from.
 */
export interface AdvancementEntry {
  id: string;
  /** ISO timestamp — "when". */
  at: string;
  kind: 'minor' | 'major' | 'level';
  option: string;
  parts: string[];
  targets: string[];
  from: number | null;
  to: number | null;
  pointsAfter: number;
  warnings: AdvancementWarning[];
  sources: string[];
}

/**
 * The character's advancement record — optional so every autosave and
 * `.atlagm` written before this feature loads untouched, and so a character
 * that has never taken a step carries no key at all.
 */
export interface AdvancementState {
  /** Banked skill points from BÜYÜK ADIM (Fate p.40: bank them, spend together). */
  points: number;
  log: AdvancementEntry[];
}

export interface Character extends EntityBase {
  campaignId: string;
  playerName: string | null;
  name: string;
  concept: string;
  sheet: CharacterSheet;
  portraitPath: string | null;
  campaignNotes: string;
  /**
   * Where this character currently is — an id from campaign.locations (the
   * World section). Optional so every autosave written before the Party
   * panel existed still loads (Phase 5 addition).
   */
  locationId?: string | null;
  /**
   * GM grants (skills/items/feats/note). Optional so every autosave and
   * `.atlagm` file written before the grant panel existed still loads.
   */
  grants?: CharacterGrants;
  /**
   * What this character has taken — KÜÇÜK ADIM / BÜYÜK ADIM steps and level
   * changes, with when, what changed and which printed page allowed it.
   * Optional for the same reason as `grants`: a character written before the
   * advancement flow existed has no key at all and derives/prints as before.
   */
  advancement?: AdvancementState;
  /**
   * The id this character had in the file it was imported from (the
   * player's own copy). A re-import of the same player's file matches on it,
   * so the GM can UPDATE the character instead of getting a duplicate.
   * Optional: characters made in this campaign have none.
   */
  importedFrom?: string | null;
}

// ---------------------------------------------------------------- People

export interface Npc extends EntityBase {
  campaignId: string;
  name: string;
  concept: string;
  attitude: 'friendly' | 'neutral' | 'hostile' | 'unknown';
  factionId: string | null;
  locationId: string | null;
  /** GM-only knowledge — never shown to players. */
  secrets: string[];
  /**
   * Served path of a portrait under /npc-art (Phase 3b — optional, so every
   * autosave written before the NPC Forge existed stays valid).
   */
  portraitPath?: string | null;
  statblockRef:
    | { kind: 'fate-array'; text: string }
    | { kind: 'dnd5e'; text: string }
    | { kind: 'custom'; text: string }
    | null;
  source: SourceRef | null;
}

export interface Enemy extends EntityBase {
  campaignId: string;
  name: string;
  role: string;
  /** Verbatim text — the app never invents numbers (§38). */
  statblock: string;
  source: SourceRef | null;
}

// ---------------------------------------------------------------- Play

/**
 * One combatant inside an encounter. The first three fields are the planner;
 * everything after them is the optional combat state the initiative tracker
 * runs on — every field is optional so every campaign saved before the
 * tracker existed loads unchanged (standing rule: new fields are optional,
 * never required).
 *
 * A character's live HP and conditions are NOT stored here: they live on the
 * character sheet (single writer — see `writeCharacterHp` in the store), and
 * the tracker reads them back from there. `currentHp` records HP for refs
 * that have no sheet (NPCs, enemies) and any hand-recorded override.
 */
export interface EncounterParticipant {
  ref: { kind: 'character' | 'npc' | 'enemy'; id: string };
  side: 'party' | 'other';
  notes: string;
  /** Total initiative: d20 + Dex modifier, or a GM's hand-typed override. */
  initiative?: number;
  /** The natural d20 face — absent when the value was typed by hand. */
  initiativeDie?: number;
  /** True once a roll (or an override) has produced `initiative`. */
  initiativeRolled?: boolean;
  /** Recorded current HP for combatants without a sheet to write to. */
  currentHp?: number;
  /** SRD condition ids (`CONDITIONS[].en` / `.tr`) toggled during this fight. */
  conditions?: string[];
}

export interface Encounter extends EntityBase {
  campaignId: string;
  name: string;
  purpose: string;
  /** zones (Fate) / initiative (5e) / freeform for mixed tables (A-08). */
  combatStyle: 'zones' | 'initiative' | 'freeform';
  participants: EncounterParticipant[];
  obstacles: string[];
  rewardNotes: string;
  status: 'planned' | 'running' | 'done';
  /** --- combat state (all optional: older autosaves carry none) --- */
  /** Current round while combat runs; defaults to 1 when absent. */
  round?: number;
  /**
   * Key of the combatant whose turn it is (`participantKey` from
   * src/domain/combat.ts), or null when combat is not running. Ending combat
   * clears this and keeps the rolled initiative.
   */
  activeTurnId?: string | null;
  /** ISO timestamp of the first turn — provenance only, never required. */
  combatStartedAt?: string;
}

// ---------------------------------------------------------------- Battle board

/**
 * One token on the Party panel's battle grid. Positions are squares on a
 * 2.5-ft grid — a play aid, not a rule (the sources do not model squares).
 * HP is tracked live during a fight: characters read it from their own sheet,
 * enemies carry their own copy seeded from the statblock's verbatim
 * "HP 58 (9d8+18)" line (the app never invents the number — §38).
 */
export interface BattleToken {
  refKind: 'character' | 'enemy';
  refId: string;
  /** Grid column/row, 0-based. */
  x: number;
  y: number;
  /** Current HP — enemy tokens only; character HP lives on the sheet. */
  hp?: number | null;
  /** Max HP parsed from the statblock, GM-correctable. */
  hpMax?: number | null;
}

export interface BattleState {
  /** Optional GameMap backdrop (its inline SVG, when it has one). */
  mapId: string | null;
  round: number;
  tokens: BattleToken[];
}

// ---------------------------------------------------------------- World

export interface Location extends EntityBase {
  campaignId: string;
  name: string;
  parentId: string | null;
  kind: string;
  description: string;
  mapId: string | null;
  factionIds: string[];
  notes: string;
}

export interface MapMarker {
  id: string;
  x: number;
  y: number;
  label: string;
  locationId: string | null;
}

/**
 * One FIGURE (token) on the battle grid — who stands where (the grid's own
 * `GameMap.tokens`, not the Party panel's HP-tracking `BattleToken` above).
 * Positions are MAP UNITS (1 unit = 5 ft), exactly like `MapMarker.x/y`, and
 * are placed on SQUARE CENTRES (snapped on placement) so a figure never
 * straddles a grid line. `label` is a PLAIN STRING frozen at creation in the
 * active locale — like `MapMarker.label`, never a bilingual pair and never a
 * reference to a character/NPC (the picker copies the name; the exported file
 * must not depend on anyone still existing).
 */
/** Which side a figure fights for — drives the disc colour and the picker's prefill. */
export type MapTokenSide = 'party' | 'enemy' | 'neutral';

export interface MapToken {
  id: string;
  /** Square centre in map units. */
  x: number;
  y: number;
  /** Plain string — the figure's name as the GM typed or picked it. */
  label: string;
  side: MapTokenSide;
  /**
   * Which tracker entity this figure STANDS FOR — the GM's explicit choice,
   * never guessed from a name match (a figure named "Aang" may well be a
   * different Aang, and a renamed character must not silently re-link).
   *
   * OPTIONAL and nullable: campaigns written before the initiative tracker
   * link existed load unchanged (`undefined`), and clearing a link simply
   * removes the key (`JSON.stringify` drops `undefined`) — either way the
   * figure carries no mark. A figure without a `ref` renders and exports
   * byte-identically to a figure from a legacy campaign.
   *
   * `occurrence` disambiguates DUPLICATE combatants: the same entity can
   * sit in an encounter's participant list twice (a second copy of the same
   * character), and the participants are keyed `kind:id` for the first,
   * `kind:id#2` for the second — the same formula `participantKey` uses in
   * domain/combat.ts, so `tokenRefKey(ref)` equals the encounter's
   * `activeTurnId` exactly. Absent `occurrence` means the first one.
   */
  ref?: MapTokenRef | null;
  /*
   * BATTLE TABLE additions — optional, so every map written before the battle
   * table loads (and prints, and exports) exactly as before; the classic grid
   * view never reads them. The table also uses `ref` above (portrait, live HP).
   */
  /** Hit points for a figure with no sheet (NPCs, enemies, extras). */
  hp?: { current: number; max: number } | null;
  /** SRD condition values (the same strings the sheets store). */
  conditions?: string[];
  /** Footprint in squares (1 = Medium, 2 = Large, 3 = Huge …). */
  size?: number;
  /** GM-only: never drawn on the broadcast screen. */
  hidden?: boolean;
}

/**
 * A figure's link to one tracker entity — `{ kind, id }` for the first
 * occurrence, `{ kind, id, occurrence: n }` for the n-th. Serialized as-is
 * inside the campaign file; see `tokenRefKey` in domain/battle-grid.ts for
 * the string key it joins on.
 */
export interface MapTokenRef {
  kind: 'character' | 'npc' | 'enemy';
  id: string;
  /** 1-based duplicate index; absent = first occurrence. */
  occurrence?: number;
}

/** An area template on the battle table (spell / bending areas). */
export interface MapTemplate {
  id: string;
  shape: 'circle' | 'cone' | 'line' | 'square';
  /** Origin in map units. */
  x: number;
  y: number;
  /** Size in feet (radius for a circle, length for a cone/line, side for a square). */
  feet: number;
  /** Direction in degrees (cone / line), 0 = east. */
  angle: number;
  color: string;
  label: string;
}

/**
 * What the broadcast screen (`#/cast`, a second window for Discord or the
 * table's TV) shows. Optional on the campaign: without it the screen shows
 * the first playable map and nothing else. Only table-safe data is ever read
 * from here — never notes, NPC secrets or statblocks.
 */
export interface BroadcastSettings {
  mapId: string | null;
  encounterId: string | null;
  /** Whose hit points the screen shows as numbers. */
  showHp: 'all' | 'party' | 'none';
  /** A handout (title, text, optional image data URL) laid over the map. */
  handout: { title: string; text: string; image: string | null } | null;
  /** Grid scale on the screen (feet per square). */
  scale: 2.5 | 5;
  /** Figures and templates are shown on the screen (off = map only). */
  showTokens: boolean;
}

export interface GameMap extends EntityBase {
  campaignId: string;
  name: string;
  /** IndexedDB blob key — local only, never uploaded. */
  imageAssetId: string | null;
  /**
   * Inline SVG for maps drawn by the Map Forge. Self-contained markup, so a
   * generated map survives export/import as plain text with no asset store.
   * Optional — maps made before the Map Forge existed simply do not have one.
   */
  svg?: string | null;
  width: number;
  height: number;
  markers: MapMarker[];
  /**
   * Figures standing on this map (`MapToken`). OPTIONAL so every campaign
   * file, autosave and `.atlagm` written before figures existed loads
   * unchanged — a map with no `tokens` key behaves exactly as before.
   */
  tokens?: MapToken[];
  /** Area templates placed on the battle table (optional, like `tokens`). */
  templates?: MapTemplate[];
}

export interface Faction extends EntityBase {
  campaignId: string;
  name: string;
  goal: string;
  influence: string;
  locationIds: string[];
  notableMembers: string[];
  notes: string;
}

export interface Item extends EntityBase {
  campaignId: string;
  name: LocalizedString;
  kind: 'equipment' | 'artifact' | 'consumable' | 'currency' | 'custom';
  description: LocalizedString;
  ownerId: string | null;
  locationId: string | null;
  source: SourceRef | null;
}

// ---------------------------------------------------------------- Story & Notes

export interface StoryNode extends EntityBase {
  campaignId: string;
  title: string;
  summary: string;
  status: 'seeded' | 'active' | 'resolved' | 'dropped';
  edges: { toId: string; label: string }[];
  linkedLocationIds: string[];
}

export type NoteLinkKind =
  | 'character'
  | 'npc'
  | 'location'
  | 'encounter'
  | 'session'
  | 'faction'
  | 'story-node'
  | 'item';

export interface Note extends EntityBase {
  campaignId: string;
  title: string;
  body: string;
  tags: string[];
  links: { kind: NoteLinkKind; id: string }[];
}
